// apps/web/src/pages/brand/SettingsBrand.tsx
import { useEffect, useState } from "react";
import * as AlertDialog from "@radix-ui/react-alert-dialog";
import { useNavigate } from "react-router-dom";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import { useTranslation } from "react-i18next";
import { useUpdateBrand, useDeleteBrand, slugify } from "@/hooks/use-brands";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import type { ApiClientError } from "@/lib/api-client";

export function BrandSettingsBrandPage() {
  const { t } = useTranslation("settings");
  const { brand } = useBrandContext();
  const { canManageBrand } = usePermissions();
  const navigate = useNavigate();

  const brandId = brand?.id ?? "";
  const updateBrand = useUpdateBrand(brandId);
  const deleteBrand = useDeleteBrand();

  const [name, setName] = useState(brand?.name ?? "");
  useEffect(() => { setName(brand?.name ?? ""); }, [brand?.name]);

  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);

  if (!brand) {
    return (
      <div className="px-6 py-8 w-full space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  const trimmed = name.trim();
  const newSlug = trimmed ? slugify(trimmed) : "";
  const dirty = trimmed !== brand.name && trimmed.length > 0;
  const slugWillChange = newSlug && newSlug !== brand.slug;

  async function handleSave() {
    if (!dirty) return;
    try {
      const updated = await updateBrand.mutateAsync({ name: trimmed });
      toast.success(t("brand.saved"));
      if (updated.slug !== brand!.slug) {
        navigate(`/${updated.slug}/settings/brand`, { replace: true });
      }
    } catch (err) {
      const apiErr = err as ApiClientError;
      if (apiErr?.code === "CONFLICT") {
        toast.error(t("brand.slugConflict"));
      } else {
        toast.error(apiErr?.message ?? t("brand.failedUpdate"));
      }
    }
  }

  async function handleDelete() {
    try {
      await deleteBrand.mutateAsync(brand!.id);
      toast.success(t("brand.deleteDialog.deleted", { name: brand!.name }));
      navigate("/dashboard");
    } catch (err) {
      const apiErr = err as ApiClientError;
      toast.error(apiErr?.message ?? t("brand.deleteDialog.failedDelete"));
    }
  }

  return (
    <div className="px-6 py-8 w-full space-y-6">
      <header>
        <h1 className="text-2xl font-semibold">{t("brand.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("brand.subtitle")}</p>
      </header>

      <section className="rounded-lg border p-4 space-y-4">
        <h2 className="text-lg font-semibold">{t("brand.details")}</h2>
        <div>
          <label className="block text-sm font-medium mb-1">{t("brand.name")}</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={!canManageBrand}
            className="block w-full max-w-md rounded border px-3 py-2 text-sm"
          />
          <p className="mt-1 text-xs text-muted-foreground">
            {t("brand.urlLabel")}<code className="font-mono">/{newSlug || brand.slug}</code>
          </p>
        </div>
        {slugWillChange && (
          <div className="rounded border border-yellow-300 bg-yellow-50 p-3 text-xs text-yellow-900">
            {t("brand.slugWillChange", { oldSlug: brand.slug, newSlug })}
          </div>
        )}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={!dirty || updateBrand.isPending || !canManageBrand}>
            {updateBrand.isPending ? t("actions.saving", { ns: "common" }) : t("actions.save", { ns: "common" })}
          </Button>
        </div>
      </section>

      {canManageBrand && (
        <section className="rounded-lg border border-destructive/40 p-4 space-y-3">
          <h2 className="text-lg font-semibold text-destructive">{t("brand.dangerZone")}</h2>
          <p className="text-sm text-muted-foreground">
            {t("brand.dangerZoneNote")}
          </p>
          <Button variant="destructive" onClick={() => { setDeleteConfirmInput(""); setDeleteOpen(true); }}>
            {t("brand.deleteBrand")}
          </Button>
        </section>
      )}

      <AlertDialog.Root open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialog.Portal>
          <AlertDialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
          <AlertDialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-background p-6 shadow-lg">
            <AlertDialog.Title className="text-lg font-semibold">{t("brand.deleteDialog.title")}</AlertDialog.Title>
            <AlertDialog.Description className="mt-2 text-sm text-muted-foreground">
              {t("brand.deleteDialog.description", { name: brand.name })}
            </AlertDialog.Description>
            <input
              value={deleteConfirmInput}
              onChange={(e) => setDeleteConfirmInput(e.target.value)}
              className="mt-3 block w-full rounded border px-3 py-2 text-sm"
              autoFocus
            />
            <div className="mt-4 flex justify-end gap-2">
              <AlertDialog.Cancel asChild>
                <Button variant="outline">{t("actions.cancel", { ns: "common" })}</Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action asChild>
                <Button
                  variant="destructive"
                  disabled={deleteConfirmInput !== brand.name || deleteBrand.isPending}
                  onClick={handleDelete}
                >
                  {deleteBrand.isPending ? t("brand.deleteDialog.deleting") : t("actions.delete", { ns: "common" })}
                </Button>
              </AlertDialog.Action>
            </div>
          </AlertDialog.Content>
        </AlertDialog.Portal>
      </AlertDialog.Root>
    </div>
  );
}
